import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Gift, ArrowLeft, Loader2 } from 'lucide-react';
import { Footer } from '@/components/Footer';

export default function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const user = await login(email, password);
            toast.success(`Bienvenue, ${user.name} !`);
            navigate(user.role === 'merchant' ? '/commercant' : '/client');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur de connexion');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-50/50 to-white flex flex-col">
            <div className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-flex items-center gap-2 mb-6 text-sm text-slate-500 hover:text-emerald-600 transition-colors" data-testid="back-home-link">
                            <ArrowLeft className="w-4 h-4" /> Retour
                        </Link>
                        <div className="flex justify-center mb-4">
                            <div className="w-20 h-20 rounded-xl flex items-center justify-center overflow-hidden">
                                <img src="/logo-fr-small.png" alt="FidelRun" className="w-20 h-20 object-contain" />
                            </div>
                        </div>
                        <h1 className="font-heading text-2xl font-bold mb-1">Connexion</h1>
                        <p className="text-sm text-slate-500">Accédez à votre espace FidelRun</p>
                    </div>

                    <Card className="border-emerald-100/50 rounded-2xl shadow-lg shadow-emerald-900/5">
                        <CardContent className="p-6">
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="votre@email.com"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        required
                                        className="h-11 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/10"
                                        data-testid="login-email-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="password" className="text-sm font-medium text-slate-700">Mot de passe</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="Votre mot de passe"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        required
                                        className="h-11 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/10"
                                        data-testid="login-password-input"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/20 active:scale-95 transition-all"
                                    data-testid="login-submit-btn"
                                >
                                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Se connecter'}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>

                    <p className="text-center mt-6 text-sm text-slate-500">
                        Pas encore de compte ?{' '}
                        <Link to="/inscription" className="text-emerald-600 font-medium hover:underline" data-testid="goto-register-link">
                            Créer un compte
                        </Link>
                    </p>
                </div>
            </div>
            <Footer />
        </div>
    );
}
